import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  ExternalLink,
  Globe,
  AlertTriangle,
  FileText,
  Send,
  Tag,
  ListChecks,
  Ban,
  CheckCircle2,
  CalendarCheck,
  DollarSign,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useQuery } from 'convex/react'
import { useAuth } from '@/contexts/auth-context'
import { api } from '@/../convex/_generated/api'
import { type DirectoryStatus, type DirectoryFlag, type PricingModel } from '../data'
import { SITE_HOST } from '@/lib/config/site'

const STATUS_STYLES: Record<DirectoryStatus, { label: string; className: string; dot: string }> = {
  accepted: { label: 'Listing is live', className: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20', dot: 'bg-emerald-500' },
  submitted: { label: 'Submitted — awaiting review', className: 'bg-amber-50 text-amber-700 ring-amber-600/20', dot: 'bg-amber-500' },
  rejected: { label: 'Rejected', className: 'bg-rose-50 text-rose-700 ring-rose-600/20', dot: 'bg-rose-500' },
  pending: { label: 'Not submitted yet', className: 'bg-neutral-100 text-neutral-500 ring-neutral-300/40', dot: 'bg-neutral-300' },
}

const FLAG_STYLES: Record<DirectoryFlag, { label: string; description: string; className: string }> = {
  'non-directory': {
    label: 'Not a directory',
    description: 'This entry is not actually a submittable product directory (blog, news site, marketplace, or service). Skip.',
    className: 'bg-rose-50 text-rose-700 ring-rose-600/20',
  },
  duplicate: {
    label: 'Duplicate',
    description: 'Likely points to the same destination as another entry.',
    className: 'bg-orange-50 text-orange-700 ring-orange-600/20',
  },
  'low-quality': {
    label: 'Low quality',
    description: 'Low DR, dead, or generally not worth the effort.',
    className: 'bg-neutral-100 text-neutral-500 ring-neutral-300/40',
  },
  review: {
    label: 'Needs review',
    description: 'Listing fit is uncertain — confirm relevance before submitting.',
    className: 'bg-amber-50 text-amber-700 ring-amber-600/20',
  },
  ineligible: {
    label: 'Not eligible',
    description: 'Footage does not meet this directory\'s eligibility criteria (geographic, vertical, or stage). Don\'t submit.',
    className: 'bg-rose-50 text-rose-700 ring-rose-600/20',
  },
  defunct: {
    label: 'Defunct',
    description: 'Site is dead, abandoned, or its submission flow is broken. Skip until further notice.',
    className: 'bg-neutral-100 text-neutral-600 ring-neutral-400/40',
  },
}

const PRICING_STYLES: Record<PricingModel, { label: string; className: string }> = {
  free: { label: 'Free', className: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20' },
  freemium: { label: 'Freemium', className: 'bg-sky-50 text-sky-700 ring-sky-600/20' },
  paid: { label: 'Paid only', className: 'bg-amber-50 text-amber-700 ring-amber-600/20' },
  'editorial-pitch': { label: 'Editorial pitch', className: 'bg-violet-50 text-violet-700 ring-violet-600/20' },
  'auto-scrape': { label: 'Auto-scrape (claim)', className: 'bg-neutral-50 text-neutral-700 ring-neutral-300/40' },
}

function dofollowBadge(value: string) {
  const v = (value || '').toLowerCase()
  if (v === 'yes' || v === 'dofollow') return { label: 'Dofollow', className: 'bg-sky-50 text-sky-700 ring-sky-600/20' }
  if (v === 'nofollow') return { label: 'Nofollow', className: 'bg-neutral-100 text-neutral-500 ring-neutral-300/40' }
  return { label: 'Unknown link type', className: 'bg-neutral-50 text-neutral-400 ring-neutral-200/60' }
}

function drColor(dr: number): string {
  if (dr >= 80) return 'text-emerald-700'
  if (dr >= 65) return 'text-emerald-600'
  if (dr >= 50) return 'text-amber-600'
  return 'text-neutral-500'
}

export default function AdminDirectoryDetailPage() {
  const { user, isAuthenticated } = useAuth()
  const isAdmin = isAuthenticated && user?.isAdmin
  const { slug } = useParams<{ slug: string }>()

  const directory = useQuery(api.directories.bySlug, isAdmin && slug ? { slug } : 'skip')

  if (!isAdmin) {
    return <div className="p-6 text-sm text-neutral-500">Admin access required</div>
  }

  if (directory === undefined) {
    return (
      <div className="min-h-screen bg-[#fafaf7]" style={{ fontFamily: '"Crimson Text", Georgia, serif' }}>
        <div className="mx-auto max-w-3xl px-4 pt-8 text-sm text-neutral-400 sm:pt-12">Loading…</div>
      </div>
    )
  }

  const d = directory
  if (!d) {
    return (
      <div className="min-h-screen bg-[#fafaf7]" style={{ fontFamily: '"Crimson Text", Georgia, serif' }}>
        <div className="mx-auto max-w-3xl space-y-4 px-4 pb-24 pt-8 sm:pt-12">
          <Link to="/admin/directories" className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700">
            <ArrowLeft className="h-4 w-4" /> All directories
          </Link>
          <div className="rounded-xl border border-neutral-200 bg-white p-6 text-sm text-neutral-500">
            No directory matches <span className="font-mono">{slug}</span>.
          </div>
        </div>
      </div>
    )
  }

  const status = STATUS_STYLES[d.status]
  const follow = dofollowBadge(d.dofollow)
  const flag = d.flag ? FLAG_STYLES[d.flag] : null
  const pricing = d.pricingModel ? PRICING_STYLES[d.pricingModel] : null
  const homepageUrl = `https://${d.target}`
  const isEligible = d.eligibility?.eligible !== false && !d.flag
  const isLive = d.status === 'accepted' && !!d.profileUrl

  return (
    <div className="min-h-screen bg-[#fafaf7]" style={{ fontFamily: '"Crimson Text", Georgia, serif' }}>
      <div className="mx-auto max-w-3xl space-y-6 px-4 pb-24 pt-8 sm:pt-12">
      <Link to="/admin/directories" className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-neutral-500 hover:text-neutral-900" style={{ fontFamily: '"JetBrains Mono", ui-monospace, monospace' }}>
        <ArrowLeft className="h-3.5 w-3.5" /> All directories
      </Link>

      <div className="flex items-start gap-4">
        <FaviconBadge target={d.target} />
        <div className="min-w-0 flex-1">
          <h1 className="text-[32px] leading-[1.05] tracking-tight text-neutral-900 sm:text-[40px]" style={{ fontFamily: '"DM Serif Display", Georgia, serif' }}>{d.name}</h1>
          <a
            href={homepageUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="text-sm text-neutral-500 hover:text-neutral-700 inline-flex items-center gap-1"
          >
            {d.target}
            <ExternalLink className="h-3 w-3" />
          </a>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <span className={cn('text-2xl font-bold tabular-nums', drColor(d.domainRating))}>DR {d.domainRating}</span>
            <span className={cn('inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full ring-1', status.className)}>
              <span className={cn('h-1.5 w-1.5 rounded-full', status.dot)} />
              {status.label}
            </span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-600 inline-flex items-center gap-1">
              <Tag className="h-3 w-3" />
              {d.category}
            </span>
            <span className={cn('text-xs font-medium px-2 py-0.5 rounded ring-1', follow.className)}>{follow.label}</span>
            {pricing && (
              <span className={cn('text-xs font-medium px-2 py-0.5 rounded ring-1 inline-flex items-center gap-1', pricing.className)}>
                <DollarSign className="h-3 w-3" />
                {pricing.label}
              </span>
            )}
            {d.verifiedAt && (
              <span className="text-[11px] text-neutral-500 inline-flex items-center gap-1" title={`Last verified ${d.verifiedAt}`}>
                <CalendarCheck className="h-3 w-3" />
                Verified {d.verifiedAt}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Top-line verdict: skip, do next, or done. */}
      {d.eligibility && d.eligibility.eligible === false ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50/50 p-4 flex items-start gap-3">
          <Ban className="h-5 w-5 mt-0.5 flex-shrink-0 text-rose-600" />
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-rose-900">Skip this one — not eligible</div>
            <div className="text-sm mt-1 text-rose-800">{d.eligibility.reason}</div>
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              {d.eligibility.geographic && (
                <span className="px-2 py-0.5 rounded-full bg-white ring-1 ring-rose-200 text-rose-700">
                  Geo: {d.eligibility.geographic}
                </span>
              )}
              {d.eligibility.vertical && (
                <span className="px-2 py-0.5 rounded-full bg-white ring-1 ring-rose-200 text-rose-700">
                  Vertical: {d.eligibility.vertical}
                </span>
              )}
              {d.eligibility.stage && (
                <span className="px-2 py-0.5 rounded-full bg-white ring-1 ring-rose-200 text-rose-700">
                  Stage: {d.eligibility.stage}
                </span>
              )}
            </div>
          </div>
        </div>
      ) : isLive ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 flex items-start gap-3">
          <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0 text-emerald-600" />
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-emerald-900">Already listed</div>
            <div className="text-sm mt-1 text-emerald-800">
              Listing is live since {d.acceptedAt || 'unknown'}. Verify the profile loads and the link still points to {SITE_HOST}.
            </div>
            {d.profileUrl && (
              <a
                href={d.profileUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1 mt-3 text-sm font-medium text-emerald-700 hover:text-emerald-900"
              >
                Open our profile <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
        </div>
      ) : isEligible && d.submissionUrl ? (
        <div className="rounded-xl border border-sky-200 bg-sky-50/50 p-4">
          <div className="flex items-start gap-3">
            <Send className="h-5 w-5 mt-0.5 flex-shrink-0 text-sky-600" />
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-sky-900">Ready to submit</div>
              <div className="text-sm mt-1 text-sky-800">
                {d.eligibility?.reason || 'No blockers — submit when you have the assets ready.'}
              </div>
              <a
                href={d.submissionUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 text-sm font-medium bg-neutral-900 text-white rounded-lg hover:bg-neutral-700"
              >
                Open submission form <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>
        </div>
      ) : null}

      {flag && (
        <div className={cn('rounded-xl border p-4 flex items-start gap-3', flag.className)}>
          <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-semibold">{flag.label}</div>
            <div className="text-sm mt-1 opacity-90">{flag.description}</div>
          </div>
        </div>
      )}

      {d.screenshotUrl && (
        <div className="rounded-xl border border-neutral-200 bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs uppercase tracking-wide text-neutral-500 inline-flex items-center gap-1.5">
              <Globe className="h-3 w-3" />
              Submission page as last verified
            </h2>
            {d.submitStatus && (
              <span
                className={cn(
                  'text-[11px] font-medium px-2 py-0.5 rounded-full ring-1',
                  d.submitStatus === 'ok' && 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
                  d.submitStatus === 'broken' && 'bg-rose-50 text-rose-700 ring-rose-600/20',
                  d.submitStatus === 'login-wall' && 'bg-amber-50 text-amber-700 ring-amber-600/20',
                  d.submitStatus === 'wrong-page' && 'bg-orange-50 text-orange-700 ring-orange-600/20',
                  d.submitStatus === 'unverified' && 'bg-neutral-100 text-neutral-500 ring-neutral-300/40',
                )}
              >
                {d.submitStatus === 'ok' && 'Real submission page'}
                {d.submitStatus === 'broken' && 'Page broken / 404'}
                {d.submitStatus === 'login-wall' && 'Behind login wall'}
                {d.submitStatus === 'wrong-page' && 'Not a submission page'}
                {d.submitStatus === 'unverified' && 'Not yet verified'}
              </span>
            )}
          </div>
          <a href={d.submissionUrl} target="_blank" rel="noreferrer noopener" className="block">
            <img
              src={d.screenshotUrl}
              alt={`Screenshot of ${d.name} submission page taken during verification`}
              className="w-full rounded-lg border border-neutral-200 hover:border-neutral-400 transition-colors"
              loading="lazy"
            />
          </a>
          <p className="text-[11px] text-neutral-400 mt-2">
            Click the screenshot to open the live page. If this looks wrong, the submit URL may have moved — please re-verify.
          </p>
        </div>
      )}

      {d.requirements && d.requirements.length > 0 && (
        <div className="rounded-xl border border-neutral-200 bg-white p-4">
          <h2 className="text-xs uppercase tracking-wide text-neutral-500 mb-3 inline-flex items-center gap-1.5">
            <ListChecks className="h-3 w-3" />
            What you need to submit
          </h2>
          <ul className="space-y-1.5">
            {d.requirements.map((req, i) => (
              <li key={i} className="text-sm text-neutral-700 flex items-start gap-2">
                <span className="text-neutral-400 mt-0.5 select-none">·</span>
                <span>{req}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {d.description && (
        <div className="rounded-xl border border-neutral-200 bg-white p-4">
          <h2 className="text-xs uppercase tracking-wide text-neutral-500 mb-2 inline-flex items-center gap-1.5">
            <FileText className="h-3 w-3" />
            About
          </h2>
          <p className="text-sm text-neutral-700 leading-relaxed whitespace-pre-line">{d.description}</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ActionCard
          icon={<Send className="h-4 w-4" />}
          title="Submission"
          href={d.submissionUrl}
          fallback="No submission URL recorded yet"
          subtext={d.fee}
        />
        <ActionCard
          icon={<Globe className="h-4 w-4" />}
          title="Footage profile"
          href={d.profileUrl}
          fallback={d.status === 'pending' ? 'Not yet submitted' : 'Awaiting acceptance'}
          subtext={d.profileUrl ? 'Live listing on this directory' : undefined}
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Round" value={d.round.toString()} />
        <Stat label="Domain Rating" value={d.domainRating.toString()} />
        <Stat label="Status" value={status.label} />
        <Stat label="Slug" value={d.slug} mono />
      </div>

      {d.fee && (
        <Section title="Fee">
          <div className="text-sm text-neutral-700">{d.fee}</div>
        </Section>
      )}

      {d.notes && (
        <Section title="Notes">
          <div className="text-sm text-neutral-700 leading-relaxed whitespace-pre-line">{d.notes}</div>
        </Section>
      )}

      <Section title="Quick links">
        <div className="flex flex-wrap gap-2">
          <ExternalPill href={homepageUrl} label="Homepage" />
          {d.submissionUrl && <ExternalPill href={d.submissionUrl} label="Submission form" />}
          {d.profileUrl && <ExternalPill href={d.profileUrl} label="Our profile" />}
          <ExternalPill
            href={`https://ahrefs.com/website-authority-checker?input=${encodeURIComponent(d.target)}&mode=subdomains`}
            label="Ahrefs DR check"
          />
          <ExternalPill
            href={`https://www.google.com/search?q=${encodeURIComponent('site:' + d.target + ' ' + SITE_HOST)}`}
            label={`Google: ${SITE_HOST} on this site`}
          />
        </div>
      </Section>
      </div>
    </div>
  )
}

function FaviconBadge({ target }: { target: string }) {
  // Google's favicon service blocks some domains; fall back to a Globe icon.
  const [errored, setErrored] = useState(false)
  const src = `https://www.google.com/s2/favicons?domain=${target}&sz=64`
  return (
    <div className="h-16 w-16 rounded-lg bg-neutral-100 flex items-center justify-center overflow-hidden border border-neutral-200">
      {errored ? (
        <Globe className="h-7 w-7 text-neutral-400" />
      ) : (
        <img
          src={src}
          alt=""
          className="h-10 w-10 object-contain"
          loading="lazy"
          onError={() => setErrored(true)}
        />
      )}
    </div>
  )
}

function ActionCard({
  icon,
  title,
  href,
  fallback,
  subtext,
}: {
  icon: React.ReactNode
  title: string
  href?: string
  fallback: string
  subtext?: string
}) {
  if (!href) {
    return (
      <div className="rounded-xl border border-dashed border-neutral-200 bg-neutral-50/50 p-4">
        <div className="text-xs uppercase tracking-wide text-neutral-500 inline-flex items-center gap-1.5">
          {icon}
          {title}
        </div>
        <div className="text-sm text-neutral-400 mt-2">{fallback}</div>
      </div>
    )
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="block rounded-xl border border-neutral-200 bg-white p-4 hover:border-neutral-400 transition-colors group"
    >
      <div className="text-xs uppercase tracking-wide text-neutral-500 inline-flex items-center gap-1.5">
        {icon}
        {title}
      </div>
      <div className="text-sm font-medium text-neutral-900 mt-2 break-words inline-flex items-center gap-1 group-hover:text-emerald-700 transition-colors">
        {href}
        <ExternalLink className="h-3 w-3 flex-shrink-0" />
      </div>
      {subtext && <div className="text-xs text-neutral-500 mt-1">{subtext}</div>}
    </a>
  )
}

function Stat({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-3">
      <div className="text-[11px] uppercase tracking-wide text-neutral-500">{label}</div>
      <div className={cn('text-sm text-neutral-900 font-medium mt-1', mono && 'font-mono text-xs')}>{value}</div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-xs uppercase tracking-wide text-neutral-500 mb-2">{title}</h2>
      <div className="rounded-xl border border-neutral-200 bg-white p-4">{children}</div>
    </div>
  )
}

function ExternalPill({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border border-neutral-300 bg-white text-neutral-700 hover:border-neutral-500 hover:text-neutral-900 transition-colors"
    >
      {label}
      <ExternalLink className="h-3 w-3" />
    </a>
  )
}

